import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeMenu, convertFormDataToRequestParams } from '@/services/api/menu-execution';
import { getPublicArtisanProducts, ArtisanProduct } from '@/services/api/artisan-product';
import { myGarageLogin, validateMyGarageToken, myGarageLogout } from '@/services/api/mygarage-auth';
import { getUserArtisanOrders } from '@/services/api/artisan-order-comments';
import { sendSMSVerification, verifySMSCode, convertToInternationalFormat, validatePhoneNumber } from '@/services/api/aws-sms-auth';
import ArtisanPurchaseModal from '@/components/modals/ArtisanPurchaseModal';
import { saveToTimeline } from '@/services/api/library';
import catSilhouette from '@/assets/cat-silhouette.png';

const MENU_ID = Number(import.meta.env.VITE_AINEKO_MENU_ID) || 47; // ウィンドウデカール 【愛猫】

type Step = 'landing' | 'login' | 'upload' | 'generating' | 'preview' | 'purchase';

const AinekoLPView: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = '愛猫ウィンドウデカール｜ペット写真からAI作成・愛車サイズにカット済 by AISHA';
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = 'ペットの写真1枚でAIがオリジナルウィンドウデカールをデザイン。愛車のウィンドウサイズにカット済みでお届けします。貼る前にイメージ確認できるから安心。猫・犬・全ペット対応。¥13,200〜（税込・配送料込）';

    // OGP画像を愛猫ロゴに変更
    const ogpUrl = 'https://storage.googleapis.com/aisha-car-images/ogp/aineko-ogp.png';
    let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
    const prevOgImage = ogImage?.content || '';
    if (ogImage) { ogImage.content = ogpUrl; }
    else { ogImage = document.createElement('meta'); ogImage.setAttribute('property', 'og:image'); ogImage.content = ogpUrl; document.head.appendChild(ogImage); }
    let twImage = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement;
    const prevTwImage = twImage?.content || '';
    if (twImage) { twImage.content = ogpUrl; }
    else { twImage = document.createElement('meta'); twImage.name = 'twitter:image'; twImage.content = ogpUrl; document.head.appendChild(twImage); }

    return () => { document.title = 'AISHA by MyGarage - 愛車をもっと楽しもう！'; meta.content = prevDesc; if (ogImage) ogImage.content = prevOgImage; if (twImage) twImage.content = prevTwImage; };
  }, []);
  const [step, setStep] = useState<Step>('landing');

  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel イベント
    const fbq = (window as any).fbq;
    if (fbq) {
      if (step === 'landing') fbq('track', 'ViewContent', { content_name: '愛猫ウィンドウデカール', content_type: 'product', currency: 'JPY' });
      if (step === 'generating') fbq('track', 'Lead', { content_name: '写真アップロード' });
      if (step === 'preview') fbq('track', 'AddToCart', { content_name: '愛猫ウィンドウデカール', content_type: 'product', currency: 'JPY' });
    }
    // Google Ads コンバージョン（写真アップロード = Lead）
    const gtag = (window as any).gtag;
    if (gtag && step === 'generating') {
      gtag('event', 'conversion', { 'send_to': 'AW-17778346232/x48sCLjEyZYcEPiRsJ1C', 'value': 13200, 'currency': 'JPY' });
      gtag('event', 'photo_upload', { 'event_category': 'engagement', 'event_label': '愛猫デカール' });
    }
  }, [step]);

  const heroSlides = [
    {
      catch1: <>あなたの<ruby className="text-orange-400"><rb>愛猫</rb><rp>(</rp><rt style={{fontSize: '0.3em', fontWeight: 'normal'}}>うちの子</rt><rp>)</rp></ruby>が、</>,
      catch2: <>窓から<span className="text-orange-400">のぞいてる</span>。</>,
      carImage: 'https://storage.googleapis.com/aisha-car-images/generated-images/admin/menu_sample_aineko_sample_280c9517-4869-44bf-a515-9aed2dd10c06.jpg',
      carName: 'ジムニーノマド',
      variationIndex: 0, // S
    },
    {
      catch1: <><span className="text-orange-400">うちの子</span>と、</>,
      catch2: <>どこへでも<span className="text-orange-400">。</span></>,
      carImage: 'https://storage.googleapis.com/aisha-car-images/lp/sienta.jpg',
      carName: 'シエンタ',
      variationIndex: 1, // M
    },
    {
      catch1: <>家族<span className="text-orange-400">みんな</span>で、</>,
      catch2: <><span className="text-orange-400">おでかけ</span>しよう。</>,
      carImage: 'https://storage.googleapis.com/aisha-car-images/lp/alphard.jpg',
      carName: 'アルファード',
      variationIndex: 2, // L
    },
  ];
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroFade, setHeroFade] = useState(true);
  useEffect(() => {
    if (step !== 'landing') return;
    const timer = setInterval(() => {
      setHeroFade(false);
      setTimeout(() => {
        setHeroIndex(prev => (prev + 1) % heroSlides.length);
        setHeroFade(true);
      }, 600);
    }, 5000);
    return () => clearInterval(timer);
  }, [step]);
  const currentSlide = heroSlides[heroIndex];

  const [petImage, setPetImage] = useState<File | null>(null);
  const [petPreviewUrl, setPetPreviewUrl] = useState<string>('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string>('');
  const [, setGeneratedImageId] = useState<string>('');
  const [error, setError] = useState('');
  const [artisanProduct, setArtisanProduct] = useState<ArtisanProduct | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [userId, setUserId] = useState<string>('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [hasOrders, setHasOrders] = useState(false);
  const [loginMode, setLoginMode] = useState<'mygarage' | 'sms'>('sms');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsSessionId, setSmsSessionId] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsNickname, setSmsNickname] = useState('');
  const [smsIsNewUser, setSmsIsNewUser] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ユーザーID取得（クッキーからMyGarageトークンを復元）
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const myGarageUser = await validateMyGarageToken();
        if (myGarageUser) {
          setUserId(myGarageUser.id);
          setUserName(myGarageUser.name || '');
          setIsLoggedIn(true);
          try {
            const orders = await getUserArtisanOrders(myGarageUser.id);
            if (orders.length > 0) setHasOrders(true);
          } catch {}
          return;
        }
      } catch (e) {
        console.error('認証確認エラー:', e);
      }
      // SMS認証のセッション復元
      const smsUserId = localStorage.getItem('aineko-sms-user-id');
      const smsUserName = localStorage.getItem('aineko-sms-user-name');
      if (smsUserId) {
        setUserId(smsUserId);
        setUserName(smsUserName || '');
        setIsLoggedIn(true);
        try {
          const orders = await getUserArtisanOrders(smsUserId);
          if (orders.length > 0) setHasOrders(true);
        } catch {}
        return;
      }
      // 保存済みのユーザー名を復元（パスワードはブラウザ自動入力に任せる）
      const savedEmail = localStorage.getItem('mygarage-saved-username') || '';
      if (savedEmail) setLoginEmail(savedEmail);
      // 旧バージョンで保存されたパスワードを削除
      localStorage.removeItem('mygarage-saved-password');
    };
    checkAuth();
  }, []);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('メールアドレスとパスワードを入力してください');
      return;
    }
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const user = await myGarageLogin(loginEmail, loginPassword);
      if (user) {
        localStorage.setItem('mygarage-saved-username', loginEmail);
        setUserId(user.id || '');
        setUserName(user.name || '');
        setIsLoggedIn(true);
        setStep('landing');
      } else {
        setLoginError('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
      }
    } catch (err) {
      setLoginError('ログインに失敗しました。');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Artisan商品を取得
  React.useEffect(() => {
    const loadProduct = async () => {
      try {
        const products = await getPublicArtisanProducts();
        if (products.length > 0) {
          setArtisanProduct(products[0]); // 最初の公開商品を使用
        }
      } catch (e) {
        console.error('商品取得エラー:', e);
      }
    };
    loadProduct();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPetImage(file);
      setPetPreviewUrl(URL.createObjectURL(file));
      setStep('upload');
      setError('');
    }
  };

  const handleGenerate = async () => {
    if (!petImage) return;

    setStep('generating');
    setError('');

    try {
      const params = convertFormDataToRequestParams({
        menu: {
          id: MENU_ID,
          engine: 'nanobanana_pro',
          useEditImage: true,
          use_edit_image: true,
        },
        editImage: petImage,
        referenceImages: [],
        promptVariables: [],
      } as any);

      const result = await executeMenu(params, userId || undefined);

      if (result && result.generatedImageUrl) {
        setGeneratedImageUrl(result.generatedImageUrl);
        setGeneratedImageId(result.frontendId || '');
        setStep('preview');
        // タイムラインに自動保存（GCSに永続URL化）
        if (userId) {
          try {
            await saveToTimeline(userId, {
              id: result.frontendId || Date.now().toString(),
              url: result.generatedImageUrl,
              displayPrompt: '愛猫ウィンドウデカール',
              menuName: '愛猫ウィンドウデカール',
              timestamp: new Date(),
              usedFormData: { menuId: MENU_ID, menuName: '愛猫ウィンドウデカール' },
              authorName: userName || 'ゲスト',
              isPublic: false,
              isSavedToLibrary: false,
            } as any);
          } catch (e) {
            console.error('タイムライン保存エラー:', e);
          }
        }
      } else {
        setError('画像の生成に失敗しました。もう一度お試しください。');
        setStep('upload');
      }
    } catch (err) {
      console.error('生成エラー:', err);
      setError('画像の生成中にエラーが発生しました。');
      setStep('upload');
    }
  };

  const handlePurchase = () => {
    setShowPurchaseModal(true);
  };

  const handleRetry = () => {
    setStep('upload');
    setGeneratedImageUrl('');
    setError('');
  };

  const scrollToUpload = () => {
    if (!isLoggedIn) {
      setStep('login');
      return;
    }
    setStep('upload');
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* ヘッダー */}
      <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
            <img src={catSilhouette} alt="" className="w-6 h-6 flex-shrink-0 invert brightness-200" />
            <div className="md:flex md:items-center md:gap-2">
              <span className="font-bold text-lg leading-tight block"><ruby>愛猫<rp>(</rp><rt style={{fontSize: '0.5em', fontWeight: 'normal'}}>うちの子</rt><rp>)</rp></ruby>デカール</span>
              <span className="text-xs text-gray-500 block">by AISHA</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/terms" target="_blank" className="text-xs text-gray-400 hover:text-white transition-colors hidden sm:inline">利用規約</a>
            <a href="/privacy" target="_blank" className="text-xs text-gray-400 hover:text-white transition-colors hidden sm:inline">プライバシーポリシー</a>
            {isLoggedIn ? (
              <>
                {hasOrders && (
                  <button
                    onClick={() => navigate('/aineko/orders')}
                    className="text-sm text-orange-400 hover:text-orange-300 border border-orange-500/50 px-3 py-1 rounded-lg transition-colors"
                  >
                    注文管理
                  </button>
                )}
                <span className="text-sm text-gray-300">{userName}</span>
                <button
                  onClick={async () => {
                    await myGarageLogout();
                    localStorage.removeItem('aineko-sms-user-id');
                    localStorage.removeItem('aineko-sms-user-name');
                    setIsLoggedIn(false);
                    setUserId('');
                    setUserName('');
                    setStep('landing');
                  }}
                  className="text-xs text-gray-400 hover:text-red-400 border border-gray-600 px-2 py-1 rounded transition-colors"
                >
                  ログアウト
                </button>
              </>
            ) : (
              <button
                onClick={() => setStep('login')}
                className="text-sm text-orange-400 hover:text-orange-300 border border-orange-500/50 px-3 py-1 rounded-lg transition-colors"
              >
                ログイン
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ヒーローセクション */}
      {step === 'landing' && (
        <section className="relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-4 py-10 md:py-14">
            <div className="text-center space-y-8">
              <div className="space-y-4">
                <h1 className={`text-4xl md:text-6xl font-black leading-tight transition-all duration-500 ${heroFade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  {currentSlide.catch1}<br />{currentSlide.catch2}
                </h1>
                <p className="text-xl md:text-2xl text-gray-400">
                  ペットの写真1枚で、世界にひとつだけのウィンドウデカールに。
                </p>
                <div className="pt-2">
                  <button
                    onClick={scrollToUpload}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-xl px-12 py-5 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
                  >
                    <span className="text-base">申し込みはこちら</span><span className="hidden md:inline"> : </span><br className="md:hidden" />ペットの写真をアップロード
                  </button>
                </div>
                <div className="flex justify-center gap-2 mt-2">
                  {heroSlides.map((_, i) => (
                    <button key={i} onClick={() => { setHeroFade(false); setTimeout(() => { setHeroIndex(i); setHeroFade(true); }, 400); }}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === heroIndex ? 'bg-orange-400 scale-125' : 'bg-gray-600 hover:bg-gray-500'}`} />
                  ))}
                </div>
              </div>

              {/* サンプル画像 + 価格 */}
              <div className="max-w-2xl mx-auto flex flex-col md:flex-row gap-4">
                {/* 左: 猫デカール縦長 */}
                <div className="md:flex-shrink-0">
                  <div className="bg-gray-800 rounded-2xl p-2 border border-gray-700 shadow-2xl">
                    <img
                      src="https://storage.googleapis.com/aisha-car-images/generated-images/admin/menu_sample_aineko_sample_efe7800e-67cd-494c-9e5a-a7954691bafe.jpg"
                      alt="リアクォーターガラス デカール"
                      className="w-full max-w-[360px] mx-auto rounded-xl"
                    />
                  </div>
                </div>
                {/* 右: ジムニー + 価格 */}
                <div className="flex-1 flex flex-col gap-4">
                  <div className="border border-gray-600 rounded-3xl overflow-hidden">
                  <div className="bg-gray-800 p-2">
                    <img
                      src={currentSlide.carImage}
                      alt={currentSlide.carName}
                      className={`w-full rounded-xl transition-all duration-500 ${heroFade ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                    />
                    <p className={`text-center text-white text-sm font-bold mt-2 transition-all duration-500 ${heroFade ? 'opacity-100' : 'opacity-0'}`}>例えば・・・ {currentSlide.carName}</p>
                    <p className="text-center text-gray-500 text-xs mt-0.5">( 愛車のウィンドウサイズにカット済 )</p>
                  </div>
                  {artisanProduct && (() => {
                    const v = artisanProduct.variations?.[currentSlide.variationIndex] || artisanProduct.variations?.[0];
                    const productPrice = artisanProduct.product_price + (v?.price || 0);
                    const installPrice = artisanProduct.installation_price + (v?.installation_price || 0);
                    return (<>
                  <div className="bg-gray-800/50 border-t border-gray-600 p-4 text-center">
                    <p className="text-white text-xl font-black">愛猫ウィンドウデカール</p>
                    <p className="text-gray-400 text-xs mt-0.5">( 猫以外のペットでも可能です。)</p>
                    <p className="text-orange-400 text-2xl font-black mt-4">{v?.name || 'サイズ S'}</p>
                    <p className="text-gray-400 text-sm mt-3">リアサイドウィンドウ</p>
                    <p className="text-gray-500 text-xs mt-1">( お乗りのお車によってサイズが異なります )</p>
                  </div>
                    <div className="bg-gray-800/50 border-t border-gray-600 p-5 text-center">
                      <p className="text-gray-400 text-sm">愛猫デカール + 配送料</p>
                      <p className="text-4xl font-black text-orange-400 mt-1">
                        ¥{Math.floor(productPrice * 1.1).toLocaleString()}<span className="text-base font-bold text-gray-400">（税込）</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">( S/M/L サイズによって価格が異なります。)</p>
                      <p className="text-xs text-gray-300 mt-0.5">ご自分でお車に貼って頂く商品です。</p>
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <p className="text-sm text-gray-300">施工希望の方は施工も可<br /><span className="text-orange-400 font-bold">+¥{Math.floor(installPrice * 1.1).toLocaleString()}（税込）</span></p>
                        <p className="text-xs text-gray-500 mt-0.5">( 神奈川県川崎市までご来店可能な方 )</p>
                      </div>
                    </div>
                  </>);
                  })()}
                  </div>
                </div>
              </div>

              {/* ステップ説明 */}
              <div className="text-center mb-6">
                <h2 className="text-2xl text-white font-black">愛猫ウィンドウデカール<span className="hidden md:inline">｜</span><br className="md:hidden" />AI作成・愛車サイズにカット済</h2>
                <p className="text-sm text-gray-400 mt-3 max-w-2xl mx-auto leading-relaxed">ペットの写真を1枚アップするだけで、AIがあなたの子だけのオリジナルデカールをデザイン。車種・ウィンドウサイズに合わせてカット済みでお届けするので、届いたら貼るだけ。うちの子と、どこへでも。</p>
              </div>

              {/* 対応車種一覧 */}
              <div className="max-w-3xl mx-auto mt-10">
                <h3 className="text-xl font-black text-white text-center mb-1">車種別・サイズ一覧</h3>
                <p className="text-gray-400 text-sm text-center mb-6">（ リアサイドウィンドウの場合 ）</p>
                <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-900/60">
                        <th className="text-left text-sm font-bold text-gray-400 px-5 py-3 w-24">サイズ</th>
                        <th className="text-left text-sm font-bold text-gray-400 px-5 py-3">車種</th>
                        <th className="text-right text-sm font-bold text-gray-400 px-5 py-3 w-32">価格（税込）</th>
                      </tr>
                    </thead>
                    <tbody>
                      {artisanProduct && [
                        { label: 'S', cars: 'ジムニー / ジムニーシエラ（ノマド含む）/ ハスラー / N-BOX / タント / スペーシア / デイズ / ルークス / アルト / ミラ', vi: 0 },
                        { label: 'M', cars: 'ノア / ヴォクシー / セレナ / ステップワゴン / シエンタ / フリード / ヴェゼル / RAV4 / ハリアー / CX-5 / エクストレイル / カローラクロス / ライズ / ロッキー', vi: 1 },
                        { label: 'L', cars: 'ランドクルーザー（250/300）/ アルファード / ヴェルファイア / エルグランド / デリカD:5 / パジェロ / ハイエース', vi: 2 },
                      ].map((row) => {
                        const v = artisanProduct.variations?.[row.vi];
                        const price = Math.floor((artisanProduct.product_price + (v?.price || 0)) * 1.1);
                        return (
                          <tr key={row.label} className="border-b border-gray-700/50">
                            <td className="text-orange-400 font-bold text-base px-5 py-3">{row.label}</td>
                            <td className="text-sm text-gray-300 px-5 py-3">{row.cars}</td>
                            <td className="text-white font-bold text-right px-5 py-3 whitespace-nowrap">¥{price.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                      <tr className="border-b border-gray-700/50">
                        <td className="text-orange-400 font-bold text-base px-5 py-3">特殊</td>
                        <td className="text-sm text-gray-300 px-5 py-3">輸入車全般 / スポーツカー（86・BRZ・ロードスター等）/ クーペ系 / 上記以外の車種</td>
                        <td className="text-white font-bold text-right px-5 py-3 whitespace-nowrap">ご相談</td>
                      </tr>
                      {artisanProduct && (() => {
                        const rows = [
                          { label: 'S (Free)', match: 'S サイズ (フリーカット)' },
                          { label: 'M (Free)', match: 'M サイズ (フリーカット)' },
                        ];
                        return rows.map((row, idx) => {
                          const v = artisanProduct.variations?.find(x => x.name.replace(/\s/g, '') === row.match.replace(/\s/g, ''));
                          const price = Math.floor((artisanProduct.product_price + (v?.price || 0)) * 1.1);
                          return (
                            <tr key={row.label} className={idx === rows.length - 1 ? 'last:border-0' : ''}>
                              <td className="text-orange-400 font-bold text-base px-5 py-3 whitespace-nowrap">{row.label}</td>
                              {idx === 0 && (
                                <td rowSpan={rows.length} className="text-sm text-gray-300 px-5 py-3 align-middle">ペットのシルエットに沿って自由な形にカット。貼る場所を選びません。ただし、貼った面には段差が生じます。気になる方は「車種別カット」がおすすめです。<br />（ 車以外の場所にも張ることができます。）</td>
                              )}
                              <td className="text-white font-bold text-right px-5 py-3 whitespace-nowrap">¥{price.toLocaleString()}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                <p className="text-center text-gray-400 text-sm mt-4">お乗りの車種が一覧にない場合もお気軽にご相談ください。<br className="md:hidden" />（税込・配送料込）</p>
                <div className="mt-4 text-center text-sm text-gray-400 space-y-1">
                  <p>対応：猫・犬・その他ペット全般</p>
                  <p>施工サービス：<span className="text-white font-bold">+¥{artisanProduct ? Math.floor(artisanProduct.installation_price * 1.1).toLocaleString() : '6,600'}（税込）</span>（来店可能な方のみ）<br className="md:hidden" /><span className="text-gray-500 md:ml-1">※ 遠い方は近郊でご紹介も可能です</span></p>
                </div>
              </div>
            </div>
          </div>
            <h3 className="text-3xl font-black text-white text-center mb-4">ご注文方法</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 max-w-[1000px] mx-auto">
                  <div className="bg-gray-800/60 border border-gray-700 rounded-xl overflow-hidden text-center">
                    <img src="https://storage.googleapis.com/aisha-car-images/generated-images/admin/menu_sample_aineko_step1_092fab1b-dd35-4bf6-bb87-610f86cd24eb.jpg" alt="STEP1" className="w-full aspect-square object-cover" />
                    <div className="px-4 py-4">
                      <p className="text-orange-400 font-bold text-base">STEP 1</p>
                      <p className="text-orange-400 text-sm mb-2">アップロード</p>
                      <p className="text-sm text-gray-300">写真を1枚アップするだけ。AIがあなたのペットだけのオリジナルデカールをデザインします。</p>
                    </div>
                  </div>
                  <div className="bg-gray-800/60 border border-gray-700 rounded-xl overflow-hidden text-center">
                    <img src="https://storage.googleapis.com/aisha-car-images/generated-images/admin/menu_sample_aineko_step2_af611f5f-7014-4334-b488-cc93939378c6.jpg" alt="STEP2" className="w-full aspect-square object-cover" />
                    <div className="px-4 py-4">
                      <p className="text-orange-400 font-bold text-base">STEP 2</p>
                      <p className="text-orange-400 text-sm mb-2">オーダーメイド</p>
                      <p className="text-sm text-gray-300">あなたの愛車に合わせてジャストフィット。どんな車種のウィンドウにもぴったりサイズで仕上げます。</p>
                    </div>
                  </div>
                  <div className="bg-gray-800/60 border border-gray-700 rounded-xl overflow-hidden text-center">
                    <img src="https://storage.googleapis.com/aisha-car-images/generated-images/admin/menu_sample_aineko_step3_4dd1ddba-48de-44bf-85ac-c383e9768d1b.jpg" alt="STEP3" className="w-full aspect-square object-cover" />
                    <div className="px-4 py-4">
                      <p className="text-orange-400 font-bold text-base">STEP 3</p>
                      <p className="text-orange-400 text-sm mb-2">仕上がりを見て決める</p>
                      <p className="text-sm text-gray-300">AIがあなたの愛車に貼ったイメージを作成。納得してからオーダー確定。キャンセルもOK。</p>
                    </div>
                  </div>
                  <div className="bg-gray-800/60 border border-gray-700 rounded-xl overflow-hidden text-center">
                    <img src="https://storage.googleapis.com/aisha-car-images/generated-images/admin/menu_sample_aineko_step4_a7437f5f-6ce4-48de-834e-e74f4c1ceb6c.jpg" alt="STEP4" className="w-full aspect-square object-cover" />
                    <div className="px-4 py-4">
                      <p className="text-orange-400 font-bold text-base">STEP 4</p>
                      <p className="text-orange-400 text-sm mb-2">届いたら貼るだけ</p>
                      <p className="text-sm text-gray-300">デカールが届いたら自分で簡単施工! 自信のない方は施工もお受けいたします。</p>
                    </div>
                  </div>
                </div>

              {/* CTA */}
              <div className="text-center py-8">
                <button
                  onClick={scrollToUpload}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-xl px-12 py-5 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
                >
                  <span className="text-base">申し込みはこちら</span><span className="hidden md:inline"> : </span><br className="md:hidden" />ペットの写真をアップロード
                </button>
                <div className="max-w-3xl mx-auto mt-16">
                  <h3 className="text-3xl font-black text-white text-center mb-2">オーダー方法</h3>
                  <p className="text-gray-400 text-center mb-8">オーダーにはログインが必要です。２つのどちらかをご利用ください。</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 relative">
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded-full">おすすめ</span>
                      <div className="w-12 h-12 border border-gray-600 rounded-full flex items-center justify-center mb-5">
                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>
                      </div>
                      <h4 className="text-xl font-bold text-white mb-3">MyGarageアカウントで利用</h4>
                      <p className="text-sm text-gray-400 mb-6 leading-relaxed">すでにMyGarageをご利用の方は、お持ちのアカウントでそのままログインできます。</p>
                      <button
                        onClick={() => { setLoginMode('mygarage'); setStep('login'); }}
                        className="w-full bg-white text-gray-900 font-bold py-3 rounded-full hover:bg-gray-200 transition-colors text-sm md:text-base"
                      >
                        MyGarageでログイン
                      </button>
                    </div>
                    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 relative">
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">マイガレージのアカウントをお持ちでない方</span>
                      <div className="w-12 h-12 border border-gray-600 rounded-full flex items-center justify-center mb-5">
                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                      </div>
                      <h4 className="text-xl font-bold text-white mb-3">電話番号で利用</h4>
                      <p className="text-sm text-gray-400 mb-6 leading-relaxed">SMS認証だけで、今すぐ簡単に始められます。面倒なパスワード設定は不要。</p>
                      <button
                        onClick={() => { setLoginMode('sms'); setStep('login'); }}
                        className="w-full bg-gray-800 text-white font-bold py-3 rounded-full border border-gray-600 hover:bg-gray-700 transition-colors text-sm md:text-base"
                      >
                        電話番号ではじめる
                      </button>
                    </div>
                  </div>

                  <p className="text-left text-white text-sm mt-5">MyGarageアカウントの作成はこちらから（別途スマホアプリからの作成となります）</p>

                  {/* MyGarage とは */}
                  <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 mt-3 flex items-center justify-between gap-6">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-white mb-2 text-left">MyGarage とは・・</h4>
                      <p className="text-sm text-gray-400 leading-relaxed">マイガレージ はカーライフを便利に快適に過ごす為の機能を備えたカーライフマネージャー。マイカーの車両管理やスケジュール管理を簡単に行うことができます。是非この機会にご登録ください。</p>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <a href="https://apps.apple.com/jp/app/mygarage-%E3%83%9E%E3%82%A4%E3%82%AC%E3%83%AC%E3%83%BC%E3%82%B8/id1277230151" target="_blank" rel="noopener noreferrer" className="bg-gray-800 text-white text-sm font-medium px-5 py-2.5 rounded-full border border-gray-600 hover:bg-gray-700 transition-colors text-center">
                        iOS版アプリ ↗
                      </a>
                      <a href="https://play.google.com/store/apps/details?id=com.hearts.mygarage&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="bg-gray-800 text-white text-sm font-medium px-5 py-2.5 rounded-full border border-gray-600 hover:bg-gray-700 transition-colors text-center">
                        Android版アプリ ↗
                      </a>
                    </div>
                  </div>
                </div>
              </div>
        </section>
      )}

      {/* ログインセクション */}
      {step === 'login' && (
        <section className="max-w-md mx-auto px-4 py-16">
          <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold">AISHA ログイン</h2>
              <p className="text-gray-400 text-sm">ご注文にはログインが必要となります。</p>
            </div>

            {/* タブ切り替え */}
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => { setLoginMode('sms'); setLoginError(''); }}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${loginMode === 'sms' ? 'text-orange-400 border-orange-400' : 'text-gray-400 border-transparent'}`}
              >
                電話番号でログイン
              </button>
              <button
                onClick={() => { setLoginMode('mygarage'); setLoginError(''); }}
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${loginMode === 'mygarage' ? 'text-orange-400 border-orange-400' : 'text-gray-400 border-transparent'}`}
              >
                MyGarageアカウント
              </button>
            </div>

            {loginMode === 'sms' ? (
              <div className="space-y-4">
                {!smsSent ? (
                  <>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">携帯電話番号</label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="09012345678"
                        onKeyDown={(e) => e.key === 'Enter' && phoneNumber && !smsLoading && (async () => {
                          if (!validatePhoneNumber(phoneNumber)) { setLoginError('正しい携帯電話番号を入力してください'); return; }
                          setSmsLoading(true); setLoginError('');
                          try {
                            const res = await sendSMSVerification(convertToInternationalFormat(phoneNumber));
                            setSmsSessionId(res.session_id); setSmsSent(true);
                          } catch (e: any) { setLoginError(e.message); }
                          finally { setSmsLoading(false); }
                        })()}
                        className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500"
                      />
                    </div>
                    {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
                    <button
                      onClick={async () => {
                        if (!validatePhoneNumber(phoneNumber)) { setLoginError('正しい携帯電話番号を入力してください'); return; }
                        setSmsLoading(true); setLoginError('');
                        try {
                          const res = await sendSMSVerification(convertToInternationalFormat(phoneNumber));
                          setSmsSessionId(res.session_id); setSmsSent(true);
                        } catch (e: any) { setLoginError(e.message); }
                        finally { setSmsLoading(false); }
                      }}
                      disabled={smsLoading || !phoneNumber}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg py-4 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {smsLoading ? '送信中...' : '認証コードを送信'}
                    </button>
                  </>
                ) : smsIsNewUser ? (
                  <>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">ニックネーム（表示名）</label>
                      <input
                        type="text"
                        value={smsNickname}
                        onChange={(e) => setSmsNickname(e.target.value)}
                        placeholder="例: たろう"
                        className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500"
                      />
                    </div>
                    {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
                    <button
                      onClick={async () => {
                        if (!smsNickname.trim()) { setLoginError('ニックネームを入力してください'); return; }
                        setSmsLoading(true); setLoginError('');
                        try {
                          const { updateUserInfo } = await import('@/services/api/aws-sms-auth');
                          await updateUserInfo(userId, smsNickname.trim());
                          setUserName(smsNickname.trim());
                          localStorage.setItem('aineko-sms-user-name', smsNickname.trim());
                          setIsLoggedIn(true);
                          setStep('landing');
                          try { const orders = await getUserArtisanOrders(userId); if (orders.length > 0) setHasOrders(true); } catch {}
                        } catch (e: any) { setLoginError(e.message); }
                        finally { setSmsLoading(false); }
                      }}
                      disabled={smsLoading}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg py-4 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {smsLoading ? '登録中...' : '登録して始める'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-400 text-center">{phoneNumber} に認証コードを送信しました</p>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">認証コード（6桁）</label>
                      <input
                        type="text"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        onKeyDown={(e) => e.key === 'Enter' && smsCode.length === 6 && !smsLoading && (async () => {
                          setSmsLoading(true); setLoginError('');
                          try {
                            const res = await verifySMSCode(smsSessionId, smsCode, convertToInternationalFormat(phoneNumber));
                            setUserId(res.user_id);
                            setUserName(res.nickname || '');
                            localStorage.setItem('aineko-sms-user-id', res.user_id);
                            localStorage.setItem('aineko-sms-user-name', res.nickname || '');
                            if (res.is_new_user) { setSmsIsNewUser(true); }
                            else { setIsLoggedIn(true); setStep('landing'); try { const orders = await getUserArtisanOrders(res.user_id); if (orders.length > 0) setHasOrders(true); } catch {} }
                          } catch (e: any) { setLoginError(e.message); }
                          finally { setSmsLoading(false); }
                        })()}
                        className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500 text-center text-2xl tracking-widest"
                      />
                    </div>
                    {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
                    <button
                      onClick={async () => {
                        setSmsLoading(true); setLoginError('');
                        try {
                          const res = await verifySMSCode(smsSessionId, smsCode, convertToInternationalFormat(phoneNumber));
                          setUserId(res.user_id);
                          setUserName(res.nickname || '');
                          localStorage.setItem('aineko-sms-user-id', res.user_id);
                          localStorage.setItem('aineko-sms-user-name', res.nickname || '');
                          if (res.is_new_user) { setSmsIsNewUser(true); }
                          else { setIsLoggedIn(true); setStep('landing'); try { const orders = await getUserArtisanOrders(res.user_id); if (orders.length > 0) setHasOrders(true); } catch {} }
                        } catch (e: any) { setLoginError(e.message); }
                        finally { setSmsLoading(false); }
                      }}
                      disabled={smsLoading || smsCode.length !== 6}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg py-4 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {smsLoading ? '認証中...' : '認証してログイン'}
                    </button>
                    <button onClick={() => { setSmsSent(false); setSmsCode(''); }} className="w-full text-gray-500 hover:text-gray-300 text-sm">
                      電話番号を変更する
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">メールアドレス</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">パスワード</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="パスワード"
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500"
                  />
                </div>
                {loginError && <p className="text-red-400 text-sm">{loginError}</p>}
                <button
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg py-4 rounded-xl transition-colors disabled:opacity-50"
                >
                  {isLoggingIn ? 'ログイン中...' : 'ログインして始める'}
                </button>
              </div>
            )}

            <button
              onClick={() => { setStep('landing'); setSmsSent(false); setSmsCode(''); setSmsIsNewUser(false); setLoginError(''); }}
              className="w-full text-gray-500 hover:text-gray-300 text-sm"
            >
              ← 戻る
            </button>
          </div>
        </section>
      )}

      {/* アップロードセクション */}
      {(step === 'upload' || step === 'landing') && step !== 'landing' && (
        <section className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold">ペットの写真をアップロード</h2>
            <p className="text-gray-400">正面から撮った写真がきれいに仕上がります</p>

            {/* アップロードエリア */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-600 rounded-2xl p-8 cursor-pointer hover:border-orange-500 transition-colors bg-gray-900/50"
            >
              {petPreviewUrl ? (
                <div className="space-y-4">
                  <img
                    src={petPreviewUrl}
                    alt="アップロードした写真"
                    className="max-h-64 mx-auto rounded-lg object-contain"
                  />
                  <p className="text-gray-400 text-sm">クリックで別の写真に変更</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-6xl">📷</div>
                  <p className="text-gray-400">クリックまたはドラッグ&ドロップ</p>
                  <p className="text-gray-500 text-sm">JPEG, PNG対応</p>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleFileSelect}
              className="hidden"
            />

            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg">
                {error}
              </div>
            )}

            {petImage && (
              <button
                onClick={handleGenerate}
                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg px-10 py-4 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 transition-all"
              >
                デカールをデザインする ✨
              </button>
            )}
          </div>

          {/* ご購入までの流れ */}
          <div className="max-w-2xl mx-auto mt-12">
            <h3 className="text-xl font-black text-white text-center mb-6">ご購入までの流れ</h3>
            <div className="space-y-4">
              {[
                { num: 1, title: '写真をアップしてデカールデザインを作成', desc: '' },
                { num: 2, title: 'バリエーションの選択', desc: '概算サイズの選択で大丈夫です。お申し込み後、お車と貼りたいウィンドウ箇所をお聞かせ頂きます。' },
                { num: 3, title: '発送先の入力', desc: '' },
                { num: 4, title: '仮申し込み（クレジット）', desc: '' },
                { num: 5, title: '確認専用ボードにてやり取り', desc: 'お客様専用の確認ボードにて、車種やウィンドウ箇所をお聞きします。' },
                { num: 6, title: '完成イメージを送付', desc: 'AIにて愛車での完成イメージをデザインし、ご確認頂きます。' },
                { num: 7, title: '正式注文 or キャンセル', desc: '完成イメージをご確認後、正式注文かキャンセルかをご選択頂きます。キャンセルの場合はクレジット決済もキャンセルとなります。手数料はかかりません。' },
                { num: 8, title: '納期のご連絡', desc: '正式注文後、納期をお知らせします。' },
                { num: 9, title: 'オリジナルデカールを作成・発送', desc: '' },
                { num: 10, title: '商品のお届け', desc: '届きましたらご確認頂き、ご自分で施工をお願いします。' },
                { num: 11, title: '評価のお願い', desc: '取り付けが完了しましたら、お車に貼られた写真と評価を是非頂ければ幸いです。' },
              ].map((step) => (
                <div key={step.num} className="flex gap-4">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">{step.num}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-base">{step.title}</p>
                    {step.desc && <p className="text-gray-400 text-sm mt-0.5 leading-relaxed">{step.desc}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 生成中 */}
      {step === 'generating' && (
        <section className="max-w-2xl mx-auto px-4 py-24">
          <div className="text-center space-y-8">
            <div className="relative">
              <div className="w-24 h-24 mx-auto">
                <div className="w-24 h-24 border-4 border-orange-500/30 rounded-full animate-spin border-t-orange-500"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl">🐱</span>
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">デカールをデザイン中...</h2>
              <p className="text-gray-400">AIがデカールをデザインしています。</p>
              <p className="text-gray-500 text-sm">30秒ほどお待ちください</p>
            </div>
          </div>
        </section>
      )}

      {/* プレビュー */}
      {step === 'preview' && (
        <section className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center space-y-6">
            <h2 className="text-2xl font-bold">デカールが完成しました！</h2>

            {/* 生成画像 */}
            <div className="bg-gray-800 rounded-2xl p-2 border border-gray-700 shadow-2xl">
              <img
                src={generatedImageUrl}
                alt="デザインされたデカール"
                className="w-full rounded-xl"
              />
            </div>

            {/* 比較 */}
            {petPreviewUrl && (
              <div className="flex items-center gap-4 justify-center">
                <div className="text-center">
                  <img src={petPreviewUrl} alt="元の写真" className="w-20 h-20 object-cover rounded-lg border border-gray-600" />
                  <p className="text-xs text-gray-500 mt-1">元の写真</p>
                </div>
                <span className="text-2xl text-orange-400">→</span>
                <div className="text-center">
                  <img src={generatedImageUrl} alt="デカール" className="w-20 h-20 object-cover rounded-lg border border-gray-600" />
                  <p className="text-xs text-gray-500 mt-1">デカール</p>
                </div>
              </div>
            )}

            {/* 料金・購入 */}
            {artisanProduct && (
              <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">ウィンドウデカール</span>
                  <span className="text-xl font-bold text-white">¥{Math.floor(artisanProduct.product_price * 1.1).toLocaleString()}〜<span className="text-sm font-normal text-gray-400">（税込）</span></span>
                </div>
                <p className="text-xs text-gray-500">配送料込（施工まで希望の方はオプションにて承ります）</p>
                {isLoggedIn ? (
                  <button
                    onClick={handlePurchase}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg py-4 rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] transition-all"
                  >
                    このデカールで申し込みをする
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-400 text-center">注文にはログインが必要です</p>
                    <button
                      onClick={() => setStep('login')}
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg py-4 rounded-xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] transition-all"
                    >
                      ログインして注文する
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleRetry}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              別の写真で作り直す
            </button>
          </div>
        </section>
      )}

      {/* Artisan購入モーダル */}
      {showPurchaseModal && artisanProduct && (
        <ArtisanPurchaseModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          userId={userId}
          imageUrl={generatedImageUrl}
          product={artisanProduct}
          onPurchaseCompleted={(orderId) => {
            const purchaseValue = Math.floor(artisanProduct.product_price * 1.1);
            // Meta Pixel Purchase
            const fbq = (window as any).fbq;
            if (fbq) {
              fbq('track', 'Purchase', { content_name: '愛猫ウィンドウデカール', content_type: 'product', value: purchaseValue, currency: 'JPY' });
            }
            // Google Ads コンバージョン（購入）
            const gtag = (window as any).gtag;
            if (gtag) {
              gtag('event', 'conversion', { 'send_to': 'AW-17778346232/4hvJCO7EyZYcEPiRsJ1C', 'value': purchaseValue, 'currency': 'JPY', 'transaction_id': orderId || '' });
              gtag('event', 'purchase', { 'currency': 'JPY', 'value': purchaseValue, 'transaction_id': orderId || '' });
            }
            setShowPurchaseModal(false);
            if (orderId) {
              navigate(`/aineko/order/${orderId}`);
            }
          }}
        />
      )}

      {/* お問い合わせ */}
      <section className="max-w-xl mx-auto px-4 py-16">
        <h3 className="text-2xl font-black text-white text-center mb-2">お問い合わせ</h3>
        <p className="text-gray-400 text-center text-sm mb-8">ご質問・ご相談はお気軽にどうぞ。</p>
        {contactSent ? (
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 text-center">
            <p className="text-lg font-bold text-white mb-2">送信しました</p>
            <p className="text-sm text-gray-400">お問い合わせありがとうございます。内容を確認の上、ご連絡いたします。</p>
          </div>
        ) : (
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 space-y-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">お名前</label>
              <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="山田 太郎" className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">メールアドレス</label>
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="example@email.com" className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">お問い合わせ内容</label>
              <textarea value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} rows={4} placeholder="ご質問やご要望をご記入ください" className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-500 resize-y" />
            </div>
            <button
              onClick={() => {
                if (!contactName || !contactEmail || !contactMessage) return;
                const subject = encodeURIComponent(`【愛猫デカール】お問い合わせ: ${contactName}`);
                const body = encodeURIComponent(`お名前: ${contactName}\nメール: ${contactEmail}\n\n${contactMessage}`);
                window.open(`mailto:info@mygare.jp?subject=${subject}&body=${body}`);
                setContactSent(true);
              }}
              disabled={!contactName || !contactEmail || !contactMessage}
              className="w-full bg-white text-gray-900 font-bold py-3 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              送信する
            </button>
          </div>
        )}
      </section>

      {/* 運営・パートナー */}
      <section className="max-w-3xl mx-auto px-4 mt-12 mb-8">
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 text-center">
            <p className="text-xs text-gray-500 mb-2">運営会社</p>
            <a href="https://2960.co.jp/" target="_blank" rel="noopener noreferrer" className="text-white font-bold text-lg hover:text-orange-400 transition-colors">株式会社ツクルマ</a>
            <p className="text-xs text-gray-500 mt-3 leading-relaxed">AIを活用して愛車を楽しむサービス AISHA を展開。「クルマを楽しむ人を増やす」をVISIONとし、カーライフプラットフォームを展開するスタートアップ企業。</p>
            <p className="text-xs text-gray-600 mt-1">神奈川県横浜市都筑区早渕3-35-17</p>
          </div>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 text-center">
            <p className="text-xs text-gray-500 mb-2">協力パートナー</p>
            <span className="text-white font-bold text-lg">ARTISAN INC</span>
            <p className="text-xs text-gray-500 mt-3 leading-relaxed">車のラッピングやプロテクションフィルムの施工を行うプロフェッショナル。レースカーだけでなく様々なクルマを手がけ、自社でデザイン、カット、施工全てを行う。</p>
            <p className="text-xs text-gray-600 mt-1">神奈川県川崎市高津区新作3丁目25-13</p>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="border-t border-gray-800">
        <div className="max-w-5xl mx-auto px-4 py-8 text-center text-gray-500 text-sm space-y-2">
          <p>愛猫デカール by AISHA × Artisan</p>
          <p>© {new Date().getFullYear()} Tsukuruma Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AinekoLPView;
